import { Entity, Not, System } from "ecsy";
import { Antibody, Attacker, BloodCell, Bullet, Collision, DamageType, Dead, Eat, ElectricConductor, Grow, HasSplit, Healed, Healer, Health, HealthSiphon, InstantDamage, OnCollision, OnDamage, OnDeath, OnKill, Physics, ReplaceSelf, Stunned, Submerged, Sync, TaggedVirus, TagProof, TimedLifespan, TradeHealth, TradeHealthOnHit, Virus, VisibleRange } from "../components";
import { addElementStat, getEntityPlayer, setElementStat } from "../composition";
import { PhysicsConstants } from "../constants";
import { Debug } from "../debug";
import { getEntityById, getId } from "../entityFactory";
import { entityTrace, getEntitySummary } from "../entityTrace";
import { Game } from "../game";
import { GameElement } from "../gameElementTypes";
import { triggerEffect } from "../triggeredEffects";
import { addComponent, getSystem } from "../utils";
import { LifeAndDeathSystem } from "./lifeAndDeathSystem";

export class DamageSystem extends System {
	game: Game;
	lifeAndDeathSystem!: LifeAndDeathSystem;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
		this.lifeAndDeathSystem = getSystem(this.world, LifeAndDeathSystem)
	}

	execute() {
		//Eaters gain hp from eating first so we don't kill them before they gain health
		const alreadyEaten = new Set<Entity>();
		this.queries.eats.added?.forEach(entity => {
			const hits = entity.getComponent(Collision).collidedWith;
			for (const hit of hits) {
				const hitEntity = hit.entity;
				if (hitEntity.hasComponent(Virus) && !alreadyEaten.has(hitEntity) && !alreadyEaten.has(entity)) {
					const eaten = this.processEating(entity, hitEntity);
					if (eaten) { alreadyEaten.add(eaten); }
				}
			}
		});

		const damageCollisions = new Array<[Entity, Entity]>();

		this.queries.collision.added?.forEach(entity => {
			const collision = entity.getComponent(Collision);
			const hits = collision.collidedWith;

			for (const hit of hits) {
				if (!hit.canDamage) {
					//Collisions between cells should stun
					const isCell = (entity: Entity) => entity.hasComponent(Antibody) || entity.hasComponent(BloodCell);
					if (isCell(entity) && isCell(hit.entity)) {
						entity.hasComponent(Antibody) && addComponent(entity, Stunned);

						const shouldSwapHealth = entity.hasComponent(TradeHealthOnHit);
						if (shouldSwapHealth) {
							entity.addComponent(TradeHealth, { target: hit.entity });
							const range = entity.getComponent(VisibleRange);
							if (range && range.excludeList) {
								// WARN: this might need to change to getId
								range.excludeList.add(hit.entity.id);
							}
						}
					}
				}
				else {
					//Damaging collision
					const attack = hit.entity.getComponent(Attacker);
					if (attack) {
						//For most things, we want the health at time of impact to determine damage
						//So collect all this for all collisions first then process all the damaging collisions later

						if (attack.damageType === DamageType.HostileCollision && hit.entity.hasComponent(Health)) {
							attack.damage = hit.entity.getComponent(Health).hp;
						}
						else if (attack.damageType === DamageType.OnlyInfect && !entity.hasComponent(BloodCell)) {
							continue;
						}
						damageCollisions.push([entity, hit.entity]);
					}
				}
			}
		});

		const completedHealthSwap = new Array<Entity>();
		for (const entity of this.queries.healthSwaps.results) {
			const id = getId(entity);
			const swap = entity.getComponent(TradeHealth);
			const health1 = entity.getComponent(Health);
			const target = getEntityById(swap.target);
			if (!id || target === null || !target.hasComponent(Health)) {
				console.warn("Invalid entity in TradeHealth block");
				continue;
			};

			const health2 = target.getComponent(Health);
			const hp1 = health1.hp;
			const hp2 = health2.hp;

			//Swap permanent health
			if (swap.isPermanent) {
				const comp1 = this.game.getCompElementById(id);
				const comp2 = this.game.getCompElementById(swap.target);
				if (!comp1 || !comp2) {
					console.warn("Invalid composition queried in TradeHealth block");
					continue;
				}
				setElementStat(comp1, 'health', hp2);
				setElementStat(comp2, 'health', hp1)
			}

			//Swap entity health now
			health1.hp = hp2;
			health2.hp = hp1;

			completedHealthSwap.push(entity);
		}
		completedHealthSwap.forEach(entity => entity.removeComponent(TradeHealth));

		//..process all the damage collisions
		for (const [collidee, collider] of damageCollisions) {
			const attack = collider.getComponent(Attacker);
			if (attack) {
				this.processDamageAndDeath(collidee, collider, attack.damage, attack.damageType);
			}
		}

		for (const entity of this.queries.instantDamage.results) {
			const damage = entity.getComponent(InstantDamage);
			const damager = damage.damagerId === -1 ? null : getEntityById(damage.damagerId);
			entityTrace(entity, `Instant damage dealt: ${damage.damage} by ${damager ? getEntitySummary(damager) : "unknown"}`);
			this.processDamageAndDeath(entity, damager, damage.damage, damage.damageType);
		}

		this.queries.lifespans.results.forEach(entity => {
			const lifespan = entity.getMutableComponent(TimedLifespan);
			const livedSteps = getStepNumber() - lifespan.firstStep;
			if (livedSteps > lifespan.stepsToLive) {
				this.kill(entity, null);
			}
		});

		this.queries.replacers.results.forEach(entity => {
			this.kill(entity, null);
		});

		//Collision triggers
		for (const entity of this.queries.collisionTrigger.results) {
			const trigger = entity.getComponent(OnCollision);

			const collision = entity.getComponent(Collision);
			//OPT: I think it might be better to just check this every frame like this rather than 
			//adding every collision component to this query don't really know for sure though.
			const hits = collision ? collision.collidedWith : [];

			//Decrement per entity cooldowns only for entities that were NOT hit this frame
			const deleteList = new Array<Entity>();
			for (const [entity, cooldown] of trigger.currentPerEntityCooldown.entries()) {
				if (cooldown <= 0) {
					deleteList.push(entity);
				}
				if (!hits.some(hit => hit.entity === entity)) {
					trigger.currentPerEntityCooldown.set(entity, cooldown - PhysicsConstants.StepRate);
				}
			}
			for (const entity of deleteList) {
				trigger.currentPerEntityCooldown.delete(entity);
			}

			//Bail out if the whole trigger is still on cooldown
			if (trigger.currentCooldown < trigger.cooldown) {
				trigger.currentCooldown += PhysicsConstants.StepRate;
				continue;
			}

			if (!collision) continue;

			for (const hit of hits) {
				//Don't trigger if have a per entity cooldown
				if (trigger.perEntityCooldown > 0) {
					if (trigger.currentPerEntityCooldown.has(hit.entity)) {
						continue;
					}
					else {
						trigger.currentPerEntityCooldown.set(hit.entity, trigger.perEntityCooldown);
					}
				}

				if (trigger.componentFilter.some(filter => hit.entity.hasComponent(filter))) {
					triggerEffect(trigger.effect, entity, hit.entity);
					trigger.currentCooldown = 0;
				}
			}


		};

		//Anything with Dead component added with an OnDeath should show here
		this.queries.deathrattles.results.forEach(entity => {
			const deathrattle = entity.getComponent(OnDeath);
			triggerEffect(deathrattle.effect, entity);
		});
	}


	processDamageAndDeath(collidee: Entity, collider: Entity | null, damage: number, damageType: DamageType) {
		if (collidee.hasComponent(TagProof) && collider && collider.hasComponent(TaggedVirus)) {
			return;
		}

		const health = collidee.getMutableComponent(Health);
		const dead = collidee.getComponent(Dead);
		health.hp -= damage;

		if (collidee.hasComponent(OnDamage)) {
			const damageTrigger = collidee.getComponent(OnDamage)
			triggerEffect(damageTrigger.effect, collidee, collider || undefined);
		}

		//Note that becaue we only trigger HealthSiphon in response to damage, multiple HSs can't chain
		if (collidee.hasComponent(HealthSiphon)) {
			const siphon = collidee.getComponent(HealthSiphon);
			if (getId(siphon.victim) !== null) { //make sure victim isn't dead this frame already
				const bloodbag = siphon.victim.getMutableComponent(Health);
				const drain = Math.min(bloodbag.hp, damage);
				bloodbag.hp -= drain;
				health.hp += drain;
				if (bloodbag.hp <= 0) {
					this.kill(siphon.victim, collidee);
				}
			}
		}

		if (health.hp <= 0 && !dead) {
			const killed = this.kill(collidee, collider);
			if (Debug.KeepTraceLogs) entityTrace(collidee, "killed by " + (collider ? getEntitySummary(collider) : "unknown source"));

			if (!collider) return;

			let killer = collider;
			let hasTrigger = collider.hasComponent(OnKill);

			// For bullets, check if owner has an OnKill trigger
			if (collider.hasComponent(Bullet) && collider.getComponent(Bullet).creatorSyncId !== undefined) {
				const creatorSyncId = collider.getComponent(Bullet).creatorSyncId!;
				const entity = getEntityById(creatorSyncId);
				if (entity) {
					hasTrigger = entity.hasComponent(OnKill);
					killer = entity;
				}
			}

			if (killed && hasTrigger) {
				const killTrigger = killer.getComponent(OnKill);
				triggerEffect(killTrigger.effect, killer, collidee);
			}
		}

		//Stats tracking
		if (damageType === DamageType.Electricity) {
			const player = getEntityPlayer(collidee, this.game);
			if (player) {
				this.game.incrementRoundStat(player.id, 'electrictyVirusKills', damage);
			}
		}
	}

	processEating(eater: Entity, eaten: Entity) {
		let eaterHealth = eater.getMutableComponent(Health);
		let eatenHealth = eaten.getMutableComponent(Health);

		//if both are eaters, the bigger one eats the smaller one, so swap if necessary
		if (eaten.hasComponent(Eat) && eatenHealth.hp > eaterHealth.hp) {
			[eater, eaten] = [eaten, eater];
			[eaterHealth, eatenHealth] = [eatenHealth, eaterHealth];
		}

		const eatComponent = eater.getComponent(Eat);
		if (eatComponent.current < eatComponent.max) {
			const consumedHp = eatenHealth.hp;
			eatenHealth.hp = 0;
			eaterHealth.hp += consumedHp;
			eatComponent.current += 1;
			if (!eater.hasComponent(Grow)) {
				addComponent(eater, Grow);
			}
			//damage processing will kill the eaten one
			return eaten;
		}
		return null;
	}

	kill(entity: Entity, killer: Entity | null) {
		if (entity.hasComponent(Dead)) return false; //Can't kill twice
		const killerInfo = {} as any;
		if (killer) {
			killerInfo.killerId = getId(killer);
			killerInfo.killerType = killer.getComponent(Sync).type;
			if (killer.hasComponent(Attacker)) {
				const attacker = killer.getMutableComponent(Attacker);
				attacker.kills += 1;
			}
		}
		addComponent(entity, Dead, killerInfo);
		entityTrace(entity, `Dead component attached. Killer: id ${killerInfo.killerId} type: ${GameElement[killerInfo.killerType]}`);

		if (entity.hasComponent(BloodCell)) {
			addComponent(entity, Submerged);  //Blood cells submerge when they die
		}

		return true;
	}
}

DamageSystem.queries = {
	eats: { components: [Eat, Collision], listen: { added: true } },
	collision: { components: [Collision, Health], listen: { added: true } },
	lifespans: { components: [TimedLifespan], listen: { added: true } },
	replacers: { components: [ReplaceSelf, Physics, HasSplit] },
	deathrattles: { components: [Dead, OnDeath] },
	healthSwaps: { components: [TradeHealth, Health] },
	instantDamage: { components: [InstantDamage, Not(Dead)] },
	collisionTrigger: { components: [OnCollision] },
};
